import {
	Divider,
	FormControlLabel,
	Radio,
	RadioGroup,
	Stack,
	Typography,
} from '@mui/material';
import React, { Fragment, useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import Icon from 'presentation/icons';
import { formatText } from 'presentation/utils';
import { ProcedureOption } from '../../types';
import { ProcedureListScrollArea, SearchResultContainer } from '../../styles';

export type ProcedureResultListProps = {
	displayedResults: ProcedureOption[];
	hasNextPage: boolean;
	loadMoreProcedures: () => void;
	isFetchingNextPage: boolean;
	searchResultsTitle: string;
	selectOptionError: string;
	codeLabel: string;
};

export function ProcedureResultList({
	displayedResults,
	hasNextPage,
	loadMoreProcedures,
	isFetchingNextPage: _isFetchingNextPage,
	searchResultsTitle,
	selectOptionError,
	codeLabel,
}: ProcedureResultListProps) {
	const { control, formState: { errors } } = useFormContext();
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const root = scrollContainerRef.current;
		const target = sentinelRef.current;
		if (!root || !target || !hasNextPage) {
			return undefined;
		}

		const observer = new IntersectionObserver(
			entries => {
				if (entries[0]?.isIntersecting) {
					loadMoreProcedures();
				}
			},
			{ root, rootMargin: '0px', threshold: 0 },
		);

		observer.observe(target);
		return () => observer.disconnect();
	}, [hasNextPage, loadMoreProcedures, displayedResults.length]);

	return (
		<Stack pt={1} gap={1}>
			<Typography color="primary" fontWeight={600}>
				{searchResultsTitle}
			</Typography>
			<ProcedureListScrollArea ref={scrollContainerRef}>
				{errors.selectedProcedure ? (
					<Typography
						variant="caption"
						color="error"
						sx={{ mt: 0.5, display: 'block' }}>
						{selectOptionError}
					</Typography>
				) : null}
				<Stack component="div">
					<Controller
						control={control}
						name="selectedProcedure"
						render={({ field }) => (
							<RadioGroup
								{...field}
								value={field.value?.code ?? ''}
								onChange={e => {
									const selected = displayedResults.find(
										o => o.value === e.target.value,
									);
									if (selected) {
										field.onChange({
											code: selected.code,
											description: selected.label,
										});
									}
								}}>
								{displayedResults.map((opt, index) => (
									<Fragment key={opt.value}>
										<FormControlLabel
											value={opt.value}
											control={
												<Radio
													sx={
														errors.selectedProcedure
															? {
																color: theme =>
																	theme.palette.error.main,
															}
															: undefined
													}
													color={
														errors.selectedProcedure
															? 'error'
															: 'primary'
													}
												/>
											}
											label={
												<SearchResultContainer>
													<Typography color="text.primary">
														{formatText(opt.label)}
													</Typography>
													<Stack
														direction="row"
														alignItems="center"
														gap="4px"
														className="procedure-code">
														<Icon
															name="tag-filled"
															color="theme.colors.gray"
															size={16}
														/>
														<Typography color="text.secondary">
															{codeLabel}
														</Typography>
														<Typography color="text.primary">
															{opt.code}
														</Typography>
													</Stack>
												</SearchResultContainer>
											}
										/>
										{index < displayedResults.length - 1 ? (
											<Divider />
										) : null}
									</Fragment>
								))}
							</RadioGroup>
						)}
					/>
					{hasNextPage ? (
						<div
							ref={sentinelRef}
							aria-hidden
							style={{ height: 1, width: '100%', flexShrink: 0 }}
						/>
					) : null}
				</Stack>
			</ProcedureListScrollArea>
		</Stack>
	);
}
